import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import NumericField from '../NumericField';
import { triggerEvent } from '../../utils';
import { LoopHeaderSection, HeaderText, Duration, DurationUnit, Categories, Category } from './styles';

const HeaderSection = ({ editor, data, payload, setPayload, translations, loading = false, loopSettings }) => {
  const { useAutoSave } = editor;
  const { hasCategories, categories: defaultCategories } = loopSettings;

  const [name, setName] = useState(data.name);
  const [duration, setDuration] = useState(data.durationHours);
  const [categoryPopupIsOpen, setCategoryPopupIsOpen] = useState(false);
  const [categories, setCategories] = useState(defaultCategories || []);

  const placeholderTexts = translations.circle.placeholder;

  useEffect(() => {
    if (data.name !== name || data.durationHours !== duration) {
      setPayload({
        ...payload,
        name,
        duration,
        categories: categories.map((category) => category.id),
        immediateSave: false,
      });
    }
  }, [name, duration]);

  useEffect(() => {
    if (JSON.stringify(categories) !== JSON.stringify(defaultCategories)) {
      setPayload({ ...payload, categories: categories.map((category) => category.id), immediateSave: true });
    }
  }, [JSON.stringify(categories)]);

  useEffect(() => {
    if (data.name !== name) {
      setName(data.name);
    }
  }, [data.name]);

  useEffect(() => {
    if (data.name !== name) {
      if (useAutoSave) {
        triggerEvent('loop.updated', { name });
      }
    }
  }, [name]);

  useEffect(() => {
    if (data.durationHours !== duration) {
      setDuration(data.durationHours);
    }
  }, [data.durationHours]);

  const setupAutogrow = () => {
    const element = document.querySelector('.autogrow');
    if (element) {
      setTimeout(() => {
        window.Autogrow(element);
      }, 100);
    }
  };

  useEffect(() => {
    if (!loading) {
      setupAutogrow();
    }
  }, [loading]);

  useEffect(() => {
    document.addEventListener('toggleDyslexicFont', setupAutogrow);
    return () => document.removeEventListener('toggleDyslexicFont', setupAutogrow);
  }, []);

  useEffect(() => {
    document.addEventListener('hideCategoryPopup', () => setCategoryPopupIsOpen(false));
    return () => document.removeEventListener('hideCategoryPopup', setCategoryPopupIsOpen);
  }, []);

  useEffect(() => {
    document.addEventListener('categoriesUpdated', (event) => setCategories(event.detail));
    return () => document.removeEventListener('categoriesUpdated', setCategories);
  }, []);

  useEffect(() => {
    if (categoryPopupIsOpen) {
      document.dispatchEvent(new CustomEvent('showCategoryPopup', { detail: categories }));
    }
  }, [categoryPopupIsOpen]);

  const categorySorter = (a, b) => {
    const getKey = ({ name, gap_type }) => `${gap_type}_${name}`;
    return getKey(a).localeCompare(getKey(b));
  };

  const mappedCategories = (categories || []).sort(categorySorter).map((category, index) => {
    const { id, gap_type, name, uuid } = category;
    const key = `categories.${gap_type}`;
    const gapType = i18n.__(key).toLowerCase();
    const displayName = `${name} (${gapType})${index === categories.length - 1 ? '' : ',&nbsp;'}`;
    return (
      <Category
        key={id}
        className={`category grade-${gap_type}`}
        dangerouslySetInnerHTML={{ __html: displayName }}
        data-category-uuid={uuid}
      />
    );
  });

  return (
    <LoopHeaderSection>
      {hasCategories && (
        <Categories onClick={() => setCategoryPopupIsOpen(true)} title={placeholderTexts.category}>
          {categories.length > 0 ? mappedCategories : <Category>{placeholderTexts.category}</Category>}
        </Categories>
      )}
      <HeaderText
        rows="1"
        aria-label={i18n.__('node_name_placeholder')}
        placeholder={i18n.__('node_name_placeholder')}
        value={name}
        onChange={(event) => {
          setName(event.target.value);
        }}
        className="autogrow"
      />
      <Duration>
        <NumericField
          id="duration"
          title={placeholderTexts.duration}
          value={duration}
          min={1}
          max={999}
          onChange={(value) => setDuration(parseInt(value))}
          hideArrows={true}
          validateOnBlur={true}
        />
        <DurationUnit>{translations.viewer.circle_duration_unit}</DurationUnit>
      </Duration>
    </LoopHeaderSection>
  );
};

export default HeaderSection;

HeaderSection.propTypes = {
  editor: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  payload: PropTypes.object.isRequired,
  setPayload: PropTypes.func.isRequired,
  translations: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  loopSettings: PropTypes.object.isRequired,
};
